import { useMemo } from 'react';

const STATIC_GENERATED_TYPE = 'static';
const DYNAMIC_GENERATED_TYPE = 'dynamic';

const STATIC_SOURCE_TYPES = new Set([
  'DEALER_VALUE',
  'TRADE_IN_VALUE',
  'OEMV',
  'FINANCE_VALUE',
  'CPO_VALUE',
]);

const DYNAMIC_SOURCE_TYPES = new Set([
  'LEASE_VEHICLE',
  'FINANCE_VEHICLE',
  'NEW_VEHICLE',
  'USED_VEHICLE',
  'CPO_VEHICLE',
]);

const TYPE_NORMALIZATION_MAP: Record<string, string> = {
  TRADE_IN: 'TRADE_IN_VALUE',
  FINANCE: 'FINANCE_VALUE',
  CPO: 'CPO_VEHICLE',
  OEM_VALUE: 'OEMV',
  OEM_VALUES: 'OEMV',
};

export function useTemplateTypeRules() {
  const staticApplicableTypeOptions = useMemo(
    () => ['LEASE_VEHICLE', 'FINANCE_VEHICLE', 'NEW_VEHICLE', 'USED_VEHICLE', 'CPO_VEHICLE'],
    [],
  );

  const normalizeTemplateType = (typeValue: string): string => {
    const normalized = (typeValue ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');
    if (!normalized) return '';
    return TYPE_NORMALIZATION_MAP[normalized] ?? normalized;
  };

  const deriveGeneratedType = (typeValue: string): string => {
    const normalized = normalizeTemplateType(typeValue);
    if (STATIC_SOURCE_TYPES.has(normalized)) return STATIC_GENERATED_TYPE;
    if (DYNAMIC_SOURCE_TYPES.has(normalized)) return DYNAMIC_GENERATED_TYPE;
    return '';
  };

  const normalizeApplicableTypes = (
    applicableTypes: unknown,
    selectedType: string,
    generatedType?: string,
  ): string[] => {
    const currentGeneratedType = generatedType ?? deriveGeneratedType(selectedType);
    const normalizedType = normalizeTemplateType(selectedType);
    const normalizedList = Array.isArray(applicableTypes)
      ? applicableTypes
      : typeof applicableTypes === 'string'
        ? applicableTypes
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
        : [];

    const cleaned = Array.from(
      new Set(normalizedList.map(item => normalizeTemplateType(String(item))).filter(Boolean)),
    );

    if (currentGeneratedType === DYNAMIC_GENERATED_TYPE) {
      return normalizedType ? [normalizedType] : [];
    }

    if (currentGeneratedType === STATIC_GENERATED_TYPE) {
      return cleaned.filter(item => staticApplicableTypeOptions.includes(item));
    }

    return cleaned;
  };

  return {
    staticApplicableTypeOptions,
    normalizeTemplateType,
    deriveGeneratedType,
    normalizeApplicableTypes,
    generatedTypeValues: {
      static: STATIC_GENERATED_TYPE,
      dynamic: DYNAMIC_GENERATED_TYPE,
    },
  };
}
